// src/modules/branches/utils/indiaHelpers.js

/**
 * India-specific utility functions for branch management
 */

// Sample pincode database (in real implementation, this would be from an API or larger dataset)
const PINCODE_DATA = {
  '110001': { city: 'New Delhi', state: 'Delhi', district: 'Central Delhi' },
  '400001': { city: 'Mumbai', state: 'Maharashtra', district: 'Mumbai City' },
  '560001': { city: 'Bangalore', state: 'Karnataka', district: 'Bangalore Urban' },
  '600001': { city: 'Chennai', state: 'Tamil Nadu', district: 'Chennai' },
  '700001': { city: 'Kolkata', state: 'West Bengal', district: 'Kolkata' },
  '500001': { city: 'Hyderabad', state: 'Telangana', district: 'Hyderabad' },
  '411001': { city: 'Pune', state: 'Maharashtra', district: 'Pune' },
  '380001': { city: 'Ahmedabad', state: 'Gujarat', district: 'Ahmedabad' },
  '302001': { city: 'Jaipur', state: 'Rajasthan', district: 'Jaipur' },
  '226001': { city: 'Lucknow', state: 'Uttar Pradesh', district: 'Lucknow' },
  '800001': { city: 'Patna', state: 'Bihar', district: 'Patna' },
  '751001': { city: 'Bhubaneswar', state: 'Odisha', district: 'Khordha' },
  '682001': { city: 'Kochi', state: 'Kerala', district: 'Ernakulam' },
  '530001': { city: 'Visakhapatnam', state: 'Andhra Pradesh', district: 'Visakhapatnam' },
  '444001': { city: 'Akola', state: 'Maharashtra', district: 'Akola' },
  '160001': { city: 'Chandigarh', state: 'Chandigarh', district: 'Chandigarh' },
  '390001': { city: 'Vadodara', state: 'Gujarat', district: 'Vadodara' },
  '395001': { city: 'Surat', state: 'Gujarat', district: 'Surat' },
  '452001': { city: 'Indore', state: 'Madhya Pradesh', district: 'Indore' },
  '462001': { city: 'Bhopal', state: 'Madhya Pradesh', district: 'Bhopal' }
};

/**
 * Fetch location details by pincode
 * In production, this would call a real API like India Post API
 */
// Fetch location data from pincode using actual API
export const fetchLocationByPincode = async (pincode) => {
  try {
    if (!pincode || pincode.length !== 6) {
      return null;
    }

    const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data && data.length > 0 && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
      const postOffice = data[0].PostOffice[0]; // Use first post office data
      
      return {
        city: postOffice.District || '', // Using District as city since it's more specific
        state: postOffice.State || '',
        country: postOffice.Country || 'India',
        region: postOffice.Region || '',
        district: postOffice.District || '',
        division: postOffice.Division || '',
        circle: postOffice.Circle || ''
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching location by pincode:', error);
    return null;
  }
};

/**
 * Generate branch code for Indian locations
 */
export const generateIndianBranchCode = (city, state, existingCodes = []) => {
  if (!city || !state) return '';
  
  // Create abbreviations for common Indian cities and states
  const cityAbbreviations = {
    'New Delhi': 'NDL',
    'Mumbai': 'MUM',
    'Bangalore': 'BLR',
    'Chennai': 'CHN', 
    'Kolkata': 'KOL',
    'Hyderabad': 'HYD',
    'Pune': 'PUN',
    'Ahmedabad': 'AMD',
    'Jaipur': 'JPR',
    'Lucknow': 'LKW',
    'Patna': 'PAT',
    'Bhubaneswar': 'BBR',
    'Kochi': 'KOC',
    'Visakhapatnam': 'VSK',
    'Chandigarh': 'CHD',
    'Vadodara': 'VDR',
    'Surat': 'SRT',
    'Indore': 'IDR',
    'Bhopal': 'BPL'
  };

  const stateAbbreviations = {
    'Delhi': 'DL',
    'Maharashtra': 'MH',
    'Karnataka': 'KA',
    'Tamil Nadu': 'TN',
    'West Bengal': 'WB',
    'Telangana': 'TG',
    'Gujarat': 'GJ',
    'Rajasthan': 'RJ',
    'Uttar Pradesh': 'UP',
    'Bihar': 'BR',
    'Odisha': 'OR',
    'Kerala': 'KL',
    'Andhra Pradesh': 'AP',
    'Chandigarh': 'CH',
    'Madhya Pradesh': 'MP'
  };

  const cityCode = cityAbbreviations[city] || city.substring(0, 3).toUpperCase();
  const stateCode = stateAbbreviations[state] || state.substring(0, 2).toUpperCase();
  
  let counter = 1;
  let suggestedCode = `${cityCode}${stateCode}${counter.toString().padStart(2, '0')}`;
  
  // Ensure the code is unique
  while (existingCodes.includes(suggestedCode)) {
    counter++;
    suggestedCode = `${cityCode}${stateCode}${counter.toString().padStart(2, '0')}`;
  }
  
  return suggestedCode;
};

/**
 * Validate Indian phone number
 */
export const validateIndianPhoneNumber = (phone) => {
  if (!phone) return false;
  
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Indian mobile number patterns
  const patterns = [
    /^[6-9]\d{9}$/, // 10 digits starting with 6-9
    /^\+91[6-9]\d{9}$/, // +91 followed by 10 digits
    /^91[6-9]\d{9}$/, // 91 followed by 10 digits
    /^0[6-9]\d{9}$/ // 0 followed by 10 digits
  ];
  
  return patterns.some(pattern => pattern.test(cleaned));
};

/**
 * Format Indian phone number for display
 */
export const formatIndianPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // Format based on pattern
  if (/^[6-9]\d{9}$/.test(cleaned)) {
    // 10 digits: 98765 43210
    return cleaned.replace(/(\d{5})(\d{5})/, '$1 $2');
  } else if (/^\+91[6-9]\d{9}$/.test(cleaned)) {
    // +91 format: +91 98765 43210
    return cleaned.replace(/(\+91)(\d{5})(\d{5})/, '$1 $2 $3');
  } else if (/^91[6-9]\d{9}$/.test(cleaned)) {
    // 91 format: +91 98765 43210
    return cleaned.replace(/^91(\d{5})(\d{5})/, '+91 $1 $2');
  }
  
  return phone; // Return original if no format matches
};

/**
 * Get Indian states list
 */
export const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh', 
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  // Union Territories
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry'
];

/**
 * Get GST state codes (useful for tax purposes)
 */
export const GST_STATE_CODES = {
  'Jammu and Kashmir': '01',
  'Himachal Pradesh': '02',
  'Punjab': '03',
  'Chandigarh': '04',
  'Uttarakhand': '05',
  'Haryana': '06',
  'Delhi': '07',
  'Rajasthan': '08',
  'Uttar Pradesh': '09',
  'Bihar': '10',
  'Sikkim': '11',
  'Arunachal Pradesh': '12',
  'Nagaland': '13',
  'Manipur': '14',
  'Mizoram': '15',
  'Tripura': '16',
  'Meghalaya': '17',
  'Assam': '18',
  'West Bengal': '19',
  'Jharkhand': '20',
  'Odisha': '21',
  'Chhattisgarh': '22',
  'Madhya Pradesh': '23',
  'Gujarat': '24',
  'Dadra and Nagar Haveli and Daman and Diu': '25',
  'Goa': '30',
  'Maharashtra': '27',
  'Karnataka': '29',
  'Andhra Pradesh': '28',
  'Telangana': '36',
  'Kerala': '32',
  'Tamil Nadu': '33',
  'Puducherry': '34',
  'Andaman and Nicobar Islands': '35',
  'Lakshadweep': '31'
};

/**
 * Check if pincode belongs to metro city
 */
export const isMetroCity = (pincode) => {
  const metroPincodeRanges = {
    'Delhi': { min: 110001, max: 110096 },
    'Mumbai': { min: 400001, max: 400104 },
    'Bangalore': { min: 560001, max: 560099 },
    'Chennai': { min: 600001, max: 600126 },
    'Kolkata': { min: 700001, max: 700156 },
    'Hyderabad': { min: 500001, max: 500097 }
  };
  
  const code = parseInt(pincode);
  return Object.values(metroPincodeRanges).some(
    range => code >= range.min && code <= range.max
  );
};

/**
 * Get business hours suggestion based on city type
 */
export const getBusinessHoursSuggestion = (pincode) => {
  const isMetro = isMetroCity(pincode);
  
  if (isMetro) {
    return {
      openTime: '09:00',
      closeTime: '19:00',
      workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    };
  } else {
    return {
      openTime: '10:00',
      closeTime: '18:00',
      workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    };
  }
};
